import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Button';
import { DndSortableList } from 'src/components/DndSortableList';
import { Grid } from 'src/components/Grid';
import { Icon } from 'src/components/Icon';
import { Stack } from 'src/components/Stack';
import { InputAdornment, TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import {
  PosConfiguration,
  PosConfigurationItem,
} from 'src/models/pos-configuration/_types';

import { usePosPreviewConfig } from './PosPreviewConfigContext';
import { getIcon } from './utils';

interface IPreviewProps {
  posConfig: PosConfiguration | null;
  selectedPage: number;
  onPageChange: (page: number) => void;
  isDarkMode: boolean;
  t: ReturnType<typeof useTranslation>['t'];
  handleReorderTiles: (
    pageIndex: number,
    newOrderedItems: PosConfigurationItem[]
  ) => void;
}

const SearchField = () => (
  <TextField
    placeholder="Search"
    fullWidth
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <Icon
            icon={icon({ name: 'search', style: 'solid' })}
            sx={{ cursor: 'pointer' }}
          />
        </InputAdornment>
      ),
      endAdornment: (
        <InputAdornment position="end">
          <Icon
            icon={icon({ name: 'barcode-read', style: 'solid' })}
            sx={{ cursor: 'pointer' }}
          />
        </InputAdornment>
      ),
    }}
  />
);

const CategoryTile = ({
  category,
  isMobile,
  isDarkMode,
}: {
  category: any;
  isMobile?: boolean;
  isDarkMode: boolean;
  t: ReturnType<typeof useTranslation>['t'];
  sortable?: any;
}) => (
  <Stack
    sx={{
      bgcolor: isDarkMode ? 'background.level1' : 'background.level2',
      borderRadius: 2,
      p: 2,
      height: '120px',
      cursor: 'grab',
      transition: 'background-color 0.3s ease',
      '&:hover': {
        bgcolor: isDarkMode ? 'background.level2' : 'background.level3',
        transition: 'background-color 0.3s ease',
      },
    }}
  >
    <Stack spacing={isMobile ? 2 : 4}>
      <Stack direction="row" justifyContent="end" color="text.primary">
        <Icon icon={getIcon(category.itemType)} sx={{ fontSize: 20 }} />
      </Stack>
      <Stack>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {category.name}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            textTransform: 'capitalize',
          }}
        >
          {category.type}
        </Typography>
      </Stack>
    </Stack>
  </Stack>
);

const PageNavigation = ({
  totalPages,
  selectedPage,
  onPageChange,
  isMobile = false,
}: {
  totalPages: number;
  selectedPage: number;
  onPageChange: (page: number) => void;
  isMobile?: boolean;
}) => (
  <Stack direction="row" spacing={isMobile ? 1 : 2}>
    {Array.from({ length: totalPages }).map((_, index) => (
      <Button
        key={index}
        variant="text"
        onClick={() => onPageChange(index)}
        sx={{
          '&:hover': {
            bgcolor: 'transparent',
          },
        }}
      >
        <Typography
          sx={
            selectedPage === index
              ? {
                  fontSize: isMobile ? 24 : 32,
                  color: 'primary.main',
                }
              : {
                  fontSize: isMobile ? 18 : 24,
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'text.primary',
                    transition: 'color 0.3s ease',
                  },
                }
          }
        >
          •
        </Typography>
      </Button>
    ))}
  </Stack>
);

const CartArea = ({ isDarkMode }: { isDarkMode: boolean }) => (
  <Stack
    sx={{
      borderRadius: 2,
      bgcolor: isDarkMode ? 'background.level1' : 'background.level2',
      height: '100%',
      width: '100%',
      transition: 'background-color 0.3s ease',
    }}
  />
);

const MobilePreview = ({
  posConfig,
  selectedPage,
  onPageChange,
  isDarkMode,
  t,
  handleReorderTiles,
}: IPreviewProps) => {
  // Prepare items with unique IDs for drag & drop
  const itemsWithIds =
    posConfig?.config[selectedPage]?.map((item, index) => ({
      ...item,
      id: (item.itemId || `tile-${selectedPage}-${index}`).toString(),
      position: item.position ?? index,
    })) || [];

  return (
    <Stack
      className={isDarkMode ? 'dark' : 'light'}
      sx={{
        width: { xs: 320, sm: 375 },
        height: { xs: 568, sm: 667 },
        bgcolor: 'background.paper',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
      }}
    >
      <Stack
        direction="column"
        sx={{
          width: '100%',
          height: '100%',
          p: 2,
        }}
        spacing={2}
      >
        <Stack
          sx={{
            flex: 7,
            width: '100%',
            height: '100%',
            overflow: 'hidden',
          }}
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack
            spacing={2}
            sx={{ width: '100%', height: 'calc(100% - 60px)' }}
          >
            <SearchField />
            <DndSortableList
              items={itemsWithIds}
              setItems={() => {
                // eslint-disable-next-line no-console
                console.log('setItems');
              }} // do not use setItems, we will manage the state manually
              onDragEnd={(event) => {
                const { active, over } = event;
                if (active?.id && over?.id && active.id !== over.id) {
                  const oldIndex = itemsWithIds.findIndex(
                    (item) => item.id === active.id
                  );
                  const newIndex = itemsWithIds.findIndex(
                    (item) => item.id === over.id
                  );
                  if (oldIndex !== -1 && newIndex !== -1) {
                    const newOrderedItems = [...itemsWithIds];
                    const [movedItem] = newOrderedItems.splice(oldIndex, 1);
                    newOrderedItems.splice(newIndex, 0, movedItem);
                    handleReorderTiles(selectedPage, newOrderedItems);
                  }
                }
              }}
              columns={2}
              sx={{
                overflow: 'auto',
                scrollbarWidth: 'none',
                width: '100%',
              }}
            >
              {(item, sortable) => (
                <CategoryTile
                  category={item}
                  isDarkMode={isDarkMode}
                  isMobile
                  t={t}
                  sortable={sortable}
                />
              )}
            </DndSortableList>
          </Stack>

          <PageNavigation
            totalPages={posConfig?.config?.length ?? 0}
            selectedPage={selectedPage}
            onPageChange={onPageChange}
            isMobile
          />
        </Stack>

        <Stack sx={{ flex: 1, width: '100%', height: '100%' }}>
          <CartArea isDarkMode={isDarkMode} />
        </Stack>
      </Stack>
    </Stack>
  );
};

const DesktopPreview = ({
  posConfig,
  selectedPage,
  onPageChange,
  isDarkMode,
  t,
  handleReorderTiles,
}: IPreviewProps) => {
  // Prepare items with unique IDs for drag & drop
  const itemsWithIds =
    posConfig?.config[selectedPage]?.map((item, index) => ({
      ...item,
      id: (item.itemId || `tile-${selectedPage}-${index}`).toString(),
      position: item.position ?? index,
    })) || [];

  return (
    <Stack
      className={isDarkMode ? 'dark' : 'light'}
      direction="row"
      sx={{
        width: '80%',
        height: '80%',
        borderRadius: 2,
        p: 4,
        bgcolor: 'background.paper',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
      }}
      spacing={2}
    >
      <Stack
        sx={{
          flex: 4,
          height: '100%',
        }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack spacing={2} sx={{ width: '100%', height: 'calc(100% - 60px)' }}>
          <SearchField />
          <DndSortableList
            items={itemsWithIds}
            setItems={() => {
              // eslint-disable-next-line no-console
              console.log('setItems');
            }} // do not use setItems, we will manage the state manually
            onDragEnd={(event) => {
              const { active, over } = event;
              if (active?.id && over?.id && active.id !== over.id) {
                const oldIndex = itemsWithIds.findIndex(
                  (item) => item.id === active.id
                );
                const newIndex = itemsWithIds.findIndex(
                  (item) => item.id === over.id
                );
                if (oldIndex !== -1 && newIndex !== -1) {
                  const newOrderedItems = [...itemsWithIds];
                  const [movedItem] = newOrderedItems.splice(oldIndex, 1);
                  newOrderedItems.splice(newIndex, 0, movedItem);
                  handleReorderTiles(selectedPage, newOrderedItems);
                }
              }
            }}
            columns={3}
            sx={{
              overflow: 'auto',
              scrollbarWidth: 'none',
              width: '100%',
            }}
          >
            {(item, sortable) => (
              <CategoryTile
                category={item}
                isDarkMode={isDarkMode}
                t={t}
                sortable={sortable}
              />
            )}
          </DndSortableList>
        </Stack>

        <PageNavigation
          totalPages={posConfig?.config?.length ?? 0}
          selectedPage={selectedPage}
          onPageChange={onPageChange}
        />
      </Stack>

      <Stack sx={{ minWidth: '300px' }}>
        <CartArea isDarkMode={isDarkMode} />
      </Stack>
    </Stack>
  );
};

const TabletPreview = ({
  posConfig,
  selectedPage,
  onPageChange,
  isDarkMode,
  t,
  handleReorderTiles,
}: IPreviewProps) => {
  // Prepare items with unique IDs for drag & drop
  const itemsWithIds =
    posConfig?.config[selectedPage]?.map((item, index) => ({
      ...item,
      id: (item.itemId || `tile-${selectedPage}-${index}`).toString(),
      position: item.position ?? index,
    })) || [];

  return (
    <Stack
      className={isDarkMode ? 'dark' : 'light'}
      direction={{ sx: 'column', lg: 'row' }}
      sx={{
        width: '80%',
        height: '80%',
        borderRadius: 2,
        p: 4,
        bgcolor: 'background.paper',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
      }}
      spacing={2}
    >
      <Stack
        sx={{
          flex: {
            xs: 7,
            lg: 4,
          },
          minWidth: '400px',
          height: { xs: 'calc(100% - 60px)', lg: '100%' },
        }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack spacing={2} sx={{ width: '100%', height: 'calc(100% - 60px)' }}>
          <SearchField />
          <DndSortableList
            items={itemsWithIds}
            setItems={() => {
              // eslint-disable-next-line no-console
              console.log('setItems');
            }} // do not use setItems, we will manage the state manually
            onDragEnd={(event) => {
              const { active, over } = event;
              if (active?.id && over?.id && active.id !== over.id) {
                const oldIndex = itemsWithIds.findIndex(
                  (item) => item.id === active.id
                );
                const newIndex = itemsWithIds.findIndex(
                  (item) => item.id === over.id
                );
                if (oldIndex !== -1 && newIndex !== -1) {
                  const newOrderedItems = [...itemsWithIds];
                  const [movedItem] = newOrderedItems.splice(oldIndex, 1);
                  newOrderedItems.splice(newIndex, 0, movedItem);
                  handleReorderTiles(selectedPage, newOrderedItems);
                }
              }
            }}
            columns={2}
            sx={{
              overflow: 'auto',
              scrollbarWidth: 'none',
              width: '100%',
            }}
          >
            {(item, sortable) => (
              <CategoryTile
                category={item}
                isDarkMode={isDarkMode}
                t={t}
                sortable={sortable}
              />
            )}
          </DndSortableList>
        </Stack>

        <PageNavigation
          totalPages={posConfig?.config?.length ?? 0}
          selectedPage={selectedPage}
          onPageChange={onPageChange}
        />
      </Stack>

      <Stack sx={{ flex: 2, minWidth: '200px' }}>
        <CartArea isDarkMode={isDarkMode} />
      </Stack>
    </Stack>
  );
};

const EmptyPreview = ({ t }: { t: ReturnType<typeof useTranslation>['t'] }) => {
  return (
    <Grid
      container
      alignItems="center"
      justifyContent="center"
      sx={{
        bgcolor: 'background.level2',
        height: 'calc(100vh - 64px)',
        width: '100%',
        transition: 'background-color 0.3s ease',
      }}
    >
      <Typography variant="h6">{t('noTemplateSelected')}</Typography>
    </Grid>
  );
};

export default function PosPreview() {
  const {
    posConfig,
    viewMode,
    theme,
    selectedPage,
    setSelectedPage,
    handleReorderTiles,
  } = usePosPreviewConfig();
  const isMobile = viewMode === 'mobile';
  const isTablet = viewMode === 'tablet';
  const isDesktop = viewMode === 'desktop';
  const isDarkMode = theme === 'dark';

  const { t } = useTranslation('translation', {
    keyPrefix: 'posPreview',
  });

  const handlePageChange = (page: number) => {
    setSelectedPage(page);
  };

  if (!posConfig?.id) {
    return <EmptyPreview t={t} />;
  }

  return (
    <Grid
      container
      alignItems="center"
      justifyContent="center"
      sx={{
        bgcolor: 'background.level2',
        height: 'calc(100vh - 64px)',
        width: '100%',
        transition: 'background-color 0.3s ease',
      }}
    >
      {isMobile && (
        <MobilePreview
          posConfig={posConfig}
          selectedPage={selectedPage}
          onPageChange={handlePageChange}
          isDarkMode={isDarkMode}
          t={t}
          handleReorderTiles={handleReorderTiles}
        />
      )}

      {isTablet && (
        <TabletPreview
          posConfig={posConfig}
          selectedPage={selectedPage}
          onPageChange={handlePageChange}
          isDarkMode={isDarkMode}
          t={t}
          handleReorderTiles={handleReorderTiles}
        />
      )}

      {isDesktop && (
        <DesktopPreview
          posConfig={posConfig}
          selectedPage={selectedPage}
          onPageChange={handlePageChange}
          isDarkMode={isDarkMode}
          t={t}
          handleReorderTiles={handleReorderTiles}
        />
      )}
    </Grid>
  );
}
